import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactElement } from "react";
import { X as XIcon } from "lucide-react";
import { color, radius, space } from "@tokens/index";
import { IconButton, Text, TextInput } from "../../primitives";
import { MentionPicker } from "../MentionPicker";
import { buildQueueNameResolver } from "../workflowRecipients";
import type { QueueSummary, QueueTreeResponse } from "../../../experience-shell/queues/types";
import { filterQueueCandidates } from "./queueCandidates";
import { ComposerWell, SendButton } from "./Messaging";
import { hairline } from "./expandMotion";
import type { StartThreadDraft } from "./types";

function stripMentionToken(value: string): string {
  const at = value.lastIndexOf("@");
  return at === -1 ? value : value.slice(0, at);
}

export function StartThreadComposer({
  queueTree,
  originatingQueueId,
  disabled,
  onStart,
}: {
  readonly queueTree: QueueTreeResponse | undefined;
  readonly originatingQueueId: string | undefined;
  readonly disabled: boolean;
  readonly onStart: (draft: StartThreadDraft) => void;
}): ReactElement {
  const [draft, setDraft] = useState("");
  const [mentioned, setMentioned] = useState<readonly QueueSummary[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const wellRef = useRef<HTMLDivElement>(null);

  const mentionFilter = useMemo(() => {
    const at = draft.lastIndexOf("@");
    if (at === -1) return null;
    const after = draft.slice(at + 1);
    if (/\s/.test(after)) return null;
    return after;
  }, [draft]);

  const excludeIds = useMemo(() => mentioned.map((q) => q.id), [mentioned]);
  const candidates = useMemo(
    () => filterQueueCandidates(queueTree, mentionFilter ?? "", excludeIds),
    [queueTree, mentionFilter, excludeIds],
  );
  const pickerOpen = mentionFilter !== null && queueTree !== undefined;
  const resolveQueueName = useMemo(() => buildQueueNameResolver(queueTree), [queueTree]);

  useEffect(() => {
    setActiveIndex(0);
  }, [mentionFilter, candidates.length]);

  const canStart =
    draft.trim().length > 0 && mentioned.length > 0 && originatingQueueId !== undefined && !disabled;

  const handlePick = useCallback((queue: QueueSummary) => {
    setDraft(stripMentionToken);
    setMentioned((current) => (current.some((q) => q.id === queue.id) ? current : [...current, queue]));
    inputRef.current?.focus();
  }, []);

  const handleDismiss = useCallback(() => {
    setDraft(stripMentionToken);
  }, []);

  const handleStart = useCallback(() => {
    const body = draft.trim();
    if (body.length === 0 || mentioned.length === 0 || originatingQueueId === undefined || disabled) {
      return;
    }
    onStart({ body, recipientQueueIds: mentioned.map((q) => q.id) });
    setDraft("");
    setMentioned([]);
    inputRef.current?.focus();
  }, [draft, mentioned, originatingQueueId, disabled, onStart]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (pickerOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (candidates.length > 0) setActiveIndex((i) => (i + 1) % candidates.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (candidates.length > 0) setActiveIndex((i) => (i - 1 + candidates.length) % candidates.length);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        handleDismiss();
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        const active = candidates[activeIndex];
        if (active !== undefined) handlePick(active);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleStart();
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: space[2], minWidth: 0 }}>
      {mentioned.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: space[2],
            padding: `${space[2]} ${space[3]}`,
            background: color.bg.surface2,
            border: hairline,
            borderRadius: radius.md,
          }}
        >
          <Text role="caps" size="xs" tone="muted">
            Asking
          </Text>
          {mentioned.map((q) => (
            <span
              key={q.id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: space[1],
                paddingLeft: space[3],
                borderRadius: radius.pill,
                background: color.bg.surface,
                border: hairline,
                color: color.fg.default,
                minWidth: 0,
              }}
            >
              <Text role="caption" weight="medium" truncate>
                {q.name}
              </Text>
              <IconButton
                icon={XIcon}
                size="sm"
                variant="ghost"
                aria-label={`Remove ${q.name}`}
                onClick={() => setMentioned((current) => current.filter((x) => x.id !== q.id))}
              />
            </span>
          ))}
        </div>
      )}
      <MentionPicker
        open={pickerOpen}
        anchorRef={wellRef}
        candidates={candidates}
        resolveQueueName={resolveQueueName}
        loading={queueTree === undefined}
        hasFilter={(mentionFilter ?? "").trim().length > 0}
        activeIndex={activeIndex}
        onActiveIndexChange={setActiveIndex}
        spawnEnabled={originatingQueueId !== undefined}
        onPick={handlePick}
        onDismiss={handleDismiss}
      />
      <ComposerWell ref={wellRef}>
        <TextInput
          ref={inputRef}
          variant="bare"
          fullWidth
          placeholder={queueTree !== undefined ? "Start a thread — type @ to pick a queue to ask" : "Start a thread…"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-label="Start a thread"
          aria-expanded={pickerOpen}
          aria-autocomplete="list"
        />
        <SendButton enabled={canStart} label="Start thread" onClick={handleStart} />
      </ComposerWell>
      {mentioned.length > 0 && originatingQueueId === undefined && (
        <Text role="caption" tone="warning" style={{ display: "block" }}>
          No queue context — open this workflow from a queue to start a thread.
        </Text>
      )}
    </div>
  );
}
